/*************************************************
 * AuditLog.jsx – PRODUCTION READY (GMPRCF)
 *************************************************/

import { useEffect, useState } from "react";

const API = import.meta.env.VITE_API_URL;


export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ================= FILTERS ================= */
  const [actorUid, setActorUid] = useState("");
  const [action, setAction] = useState("");
  const [resource, setResource] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  /* ================= PAGINATION ================= */
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  /* ================= FETCH LOGS ================= */
  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");

      const params = new URLSearchParams({
        page,
        limit,
      });

      if (actorUid) params.append("actor_uid", actorUid);
      if (action) params.append("action", action);
      if (resource) params.append("resource", resource);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);

      const res = await fetch(`${API}/activity-log?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to load activity logs");
      }

      const data = await res.json();

      setLogs(data.data);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page]);

  /* ================= CLEAR LOGS ================= */
  const clearLogs = async (beforeDate = null) => {
    if (!window.confirm("Are you sure you want to clear logs?")) return;

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API}/activity-log`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          beforeDate ? { before_date: beforeDate } : {}
        ),
      });

      if (!res.ok) {
        throw new Error("Failed to clear logs");
      }

      setPage(1);
      fetchLogs();
    } catch (err) {
      alert(err.message);
    }
  };

  /* ================= RENDER ================= */
  return (
    <div style={{ padding: "20px" }}>
      <h2>Audit Log</h2>

      {/* FILTERS */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <input
          placeholder="Actor UID"
          value={actorUid}
          onChange={(e) => setActorUid(e.target.value)}
        />

        <select value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">All Actions</option>
          <option value="READ">READ</option>
          <option value="CREATE">CREATE</option>
          <option value="UPDATE">UPDATE</option>
          <option value="DELETE">DELETE</option>
        </select>

        <input
          placeholder="Resource"
          value={resource}
          onChange={(e) => setResource(e.target.value)}
        />

        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />

        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />

        <button onClick={() => { setPage(1); fetchLogs(); }}>
          Apply Filters
        </button>
      </div>

      {/* ACTIONS */}
      <div style={{ margin: "10px 0" }}>
        <button onClick={() => clearLogs(null)}>
          Clear All Logs
        </button>{" "}
        <button
          onClick={() =>
            toDate
              ? clearLogs(toDate)
              : alert("Select a 'To Date' first")
          }
        >
          Clear Before Date
        </button>
      </div>

      {/* TABLE */}
      {loading && <p>Loading…</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {!loading && (
        <table width="100%" border="1" cellPadding="6">
          <thead>
            <tr>
              <th>Date</th>
              <th>User</th>
              <th>Role</th>
              <th>Action</th>
              <th>Resource</th>
              <th>Resource ID</th>
              <th>Status</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && (
              <tr>
                <td colSpan="8" align="center">
                  No logs found
                </td>
              </tr>
            )}

            {logs.map((log) => (
              <tr key={log.id}>
                <td>{new Date(log.created_at).toLocaleString()}</td>
                <td>{log.actor_uid}</td>
                <td>{log.actor_usertype}</td>
                <td>{log.action}</td>
                <td>{log.resource}</td>
                <td>{log.resource_id || "-"}</td>
                <td>{log.status}</td>
                <td>{log.remarks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* PAGINATION */}
      <div style={{ marginTop: "10px" }}>
        <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>
          Prev
        </button>{" "}
        Page {page} / {Math.ceil(total / limit) || 1}{" "}
        <button
          disabled={page >= Math.ceil(total / limit)}
          onClick={() => setPage(p => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
